import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Utils
import { getMonthDropdown } from 'app/shared/utils/dateUtils';
import stringHelper from 'app/shared/utils/stringHelper';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Select from '@zg-rentals/multibrand-ui/lib/Select';

import { Link as v4Link } from 'react-router-dom';

// Actions
import AnalyticsActions from 'app/shared/actions/AnalyticsActions';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import sharedConfig from 'app/shared/sharedConfig';
import AddressColumn from 'app/shared/components/Analytics/AddressColumn';
import Text from '@zg-rentals/multibrand-ui/lib/Text';

// TODO: update mui Row, Col to use zrs-comet
import { Container } from '@zillow/zrs-comet';

// Selectors
import { AppSelectors, AnalyticsSelectors } from 'app/shared/selectors';

import pathsManifest from 'app/shared/pathsManifest';

import TableMFH from 'app/shared/components/TableMFH';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'Funnel Analytics';
const ANALYTICS_CATEGORY = 'Funnel Page';
const MONTH_DROPDOWN = getMonthDropdown();

const propTypes = {
    propertiesReport: PropTypes.object.isRequired,
};

const Error = ({ message }) => (
    <Container>
        <Row>
            <Col>
                <Text className="error">
                    {message}
                </Text>
            </Col>
        </Row>
    </Container>
);

class FunnelPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isLoading: true,
            startDate: MONTH_DROPDOWN[0].value,
        };

        this.handlePropertyClick = this.handlePropertyClick.bind(this);
        this.handleDownloadClick = this.handleDownloadClick.bind(this);
        this.handleReviewsClick = this.handleReviewsClick.bind(this);
        this.getPropertiesReportData = this.getPropertiesReportData.bind(this);
    }

    componentDidMount() {
        this.getPropertiesReportData(this.state.startDate);
    }

    handlePropertyClick(listingKey) {
        const { dispatch } = this.props;

        dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.PROPERTY_COLUMN_CLICK, {
                category: ANALYTICS_CATEGORY,
            })
        );

        const paths = pathsManifest.getAllPaths();
        const url = `${paths.propertiesPage}/${listingKey}/${paths.propertyDetailsListing}`;
        window.router.transitionTo(url);
    }

    handleReviewsClick(listingKey) {
        const { dispatch } = this.props;
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.REVIEW_COLUMN_CLICK));
        const paths = pathsManifest.getAllPaths();
        const url = `${paths.propertiesPage}/${listingKey}/${paths.propertyDetailsReviews}`;
        window.router.transitionTo(url);
    }

    handleDownloadClick() {
        this.props.dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.DOWNLOAD_DATA_CLICK, {
            category: ANALYTICS_CATEGORY,
        }));
    }

    getPropertiesReportData(startDate) {
        const params = {
            startDateYyyyMmDd: startDate,
        };

        this.setState({
            isLoading: true,
            startDate,
        });
        this.props.dispatch(AnalyticsActions.getPropertiesReport(params))
            .then((result) => {
                const data = result.payload;
                if (data.success === false) {
                    this.setState({
                        isLoading: false,
                        error: 'Sorry, something went wrong. Please try again later.',
                    });
                } else {
                    this.setState({ isLoading: false });
                }
            }).catch((err) => {
                global.TEMPORARY_global_logger.error(err);
            });
    }

    getColumnConfig(isMobile, hasViewData, hasLeadData, hasLeaseData, hasClickData, hasDaysOnMarketData) {
        const columnConfig = [];

        if (hasViewData) {
            columnConfig.push({
                dataKey: 'totalViews',
                width: 1,
                header: 'Views',
                align: 'right',
                isSortable: true,
            });
        }
        if (hasLeadData) {
            columnConfig.push({
                dataKey: 'totalLeads',
                width: 1,
                header: 'Leads',
                align: 'right',
                isSortable: true,
            });
        }
        if (hasDaysOnMarketData) {
            columnConfig.push({
                dataKey: 'totalDaysOnMarket',
                width: 1,
                header: 'Days on market',
                align: 'right',
                isSortable: true,
            });
        }
        if (hasLeaseData && !isMobile) {
            columnConfig.push({
                dataKey: 'totalLeases',
                width: 1,
                header: 'Leases',
                align: 'right',
                isSortable: true,
            });
        }
        if (hasClickData && !isMobile) {
            columnConfig.push({
                dataKey: 'totalClicks',
                width: 1,
                header: 'Website Referrals',
                align: 'right',
                isSortable: true,
            });
        }
        if (!isMobile) {
            columnConfig.push({
                dataKey: 'averageStarLevel',
                width: 1,
                header: 'Reviews',
                align: 'right',
                isSortable: true,
            });
        }
        const totalWidth = Math.ceil((columnConfig.length + 1) / 0.8);
        const headerColWidth = totalWidth - columnConfig.length;
        columnConfig.unshift({
            header: 'Advertising Type',
            width: 1,
            renderer: data => (data.listing.productTypes.length > 0
                ? data.listing.productTypes.map((productType, index) => <Text key={index}>{productType}</Text>)
                : '-'),

        });
        columnConfig.unshift({
            dataKey: 'propertyName',
            sortType: 'string',
            width: headerColWidth,
            header: 'Property',
            isSortable: true,
            renderer: data => (
                <AddressColumn
                    index={data.index}
                    listing={data.listing}
                    listingKey={data.listingKey}
                    onClick={data.listing.hasAccess ? this.handlePropertyClick : undefined}
                    useAddress={data.totalDaysOnMarket > 0}
                />
            ),
        });
        return columnConfig;
    }

    getRowList(isMobile, propertiesReportModel, hasViewData, hasLeadData, hasLeaseData, hasClickData) {
        const rows = Object.values(propertiesReportModel.rows);
        const listings = propertiesReportModel.propertyDetails;
        return rows.map((data, index) => {
            const listingKey = `${data.feedListingId}_${data.feedId}`;
            const listing = listings[listingKey] || {};
            const rowData = {
                listingKey,
                listing,
                propertyName: data.totalDaysOnMarket > 0 ? listing.street : listing.name || listing.street,
                index,
            };

            rowData.totalViews = data.viewCount;
            rowData.totalLeads = data.leadCount;
            rowData.totalDaysOnMarket = data.daysOnMarketCount;

            if (hasLeaseData && !isMobile) {
                rowData.totalLeases = data.leaseCount;
            }
            if (hasClickData && !isMobile) {
                rowData.totalClicks = data.clickCount;
            }
            if (!isMobile) {
                rowData.averageStarLevel = data.averageStarLevel ?
                    (
                        <Link
                            reactRouterLink={v4Link}
                            onClick={() => this.handleReviewsClick(listingKey)}
                        >
                            {stringHelper.pluralize(data.averageStarLevel, 'star', 'stars')}
                        </Link>
                    ) :
                    '-';
            }
            return rowData;
        });
    }

    getTableData(propertiesReportModel, isMobile) {
        let columnConfig = [];
        let rowList = [];

        if (propertiesReportModel && propertiesReportModel.rows) {
            const hasLeadData = propertiesReportModel.leadCount > 0;
            const hasClickData = propertiesReportModel.clickCount > 0;
            const hasLeaseData = false;
            const hasViewData = propertiesReportModel.viewCount > 0;
            const hasDaysOnMarketData = propertiesReportModel.daysOnMarketCount > 0;

            columnConfig = this.getColumnConfig(isMobile, hasViewData, hasLeadData, hasLeaseData, hasClickData,
                hasDaysOnMarketData);
            rowList = this.getRowList(isMobile, propertiesReportModel, hasViewData, hasLeadData, hasLeaseData,
                hasClickData);
        }

        return {
            rowList,
            columnConfig,
        };
    }

    render() {
        const { startDate, isLoading, error } = this.state;
        const { propertiesReport, isScreenMobile } = this.props;
        const paths = pathsManifest.getAllPaths();
        const excelPath = paths.exportExcel.properties;
        const startDateParam = startDate ? `?startDateYyyyMmDd=${startDate}` : '';
        const excelUrl = `${paths.proxy}/${sharedConfig.baseApi}${excelPath}${startDateParam}`;

        const {
            rowList,
            columnConfig,
        } = this.getTableData(propertiesReport, isScreenMobile);

        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <div className="FunnelPage">
                    <HeadWrapper
                        title={DEFAULT_PAGE_TITLE}
                    />
                    <Row className="FunnelPage-top-row">
                        <Col sm={3} className="FunnelPage-col">
                            <Select
                                disabled={isLoading}
                                name="startDate"
                                onChange={e => this.getPropertiesReportData(e.target.value)}
                            >
                                {MONTH_DROPDOWN.map(({ value, name }) => (
                                    <option
                                        key={value}
                                        value={value}
                                    >
                                        {name}
                                    </option>
                                ))}
                            </Select>
                        </Col>
                        <Col sm={6} className="FunnelPage-col" />
                        <Col sm={3} className="FunnelPage-col FunnelPage-export">
                            {!isLoading && (
                                <Link
                                    reactRouterLink={v4Link}
                                    to={excelUrl}
                                    target="_blank"
                                    onClick={this.handleDownloadClick}
                                >
                                    Export details
                                </Link>
                            )}
                        </Col>
                    </Row>
                    <TableMFH
                        idKey="funnel"
                        columnConfig={columnConfig}
                        data={isLoading ? [] : rowList}
                        ErrorRowComponent={error ? () => <Error message={error} /> : undefined}
                        isLoading={isLoading}
                        columnDivisions={columnConfig.length ? (columnConfig[0].width + columnConfig.length - 1) : 1}
                        defaultSortKey={{
                            key: 'propertyName',
                            ascending: true,
                        }}
                    />
                    {(!isLoading && rowList.length === 0 && !error) &&
                        <Error message="No properties found." />
                    }
                </div>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    propertiesReport: AnalyticsSelectors.makePropertiesReport(state),
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
});

FunnelPage.propTypes = propTypes;

export { FunnelPage };
export default connect(mapStateToProps)(FunnelPage);
